import { useState } from 'react';
import { leadsApi } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { UpdateExistingLeadDialog } from '../../components/dialogs/UpdateExistingLeadDialog';

import { CustomColumnDefinition } from '../../types/lead';

interface EditLeadData {
  existingLead: {
    id: string;
    name: string;
    phone: string;
    source?: string;
    status?: string;
    notes?: string;
    custom_fields?: Record<string, any>;
  };
  inputData: {
    name: string;
    phone: string;
    notes?: string;
    custom_fields?: Record<string, any>;
    status?: string;
  };
}

interface UseEditLeadDialogProps {
  onSuccess: (updatedLead?: any) => void;
  onStatusUpdated?: () => void;
  smbId?: string;
  customColumns?: CustomColumnDefinition[];
  customStatuses?: string[];
  setCustomStatuses?: React.Dispatch<React.SetStateAction<string[]>>;
}

export const useEditLeadDialog = ({ onSuccess, onStatusUpdated, smbId, customColumns, customStatuses, setCustomStatuses }: UseEditLeadDialogProps = { onSuccess: () => {} }) => {
  const [editLeadData, setEditLeadData] = useState<EditLeadData | null>(null);
  const [leadDialogMode, setLeadDialogMode] = useState<'edit' | 'view' | 'conflict'>('edit');
  const [isUpdatingLead, setIsUpdatingLead] = useState(false);
  const { showNotification } = useNotification();

  const openEditLead = async (
    leadId: string, 
    leadName: string, 
    leadPhone: string, 
    source?: string, 
    status?: string,
    mode: 'edit' | 'view' | 'conflict' = 'edit',
    inputName?: string,
    notes?: string,
    customFields?: Record<string, any>,
    inputNotes?: string,
    inputCustomFields?: Record<string, any>,
    inputStatus?: string
  ) => {
    setLeadDialogMode(mode);
    setEditLeadData({
      existingLead: {
        id: leadId,
        name: leadName,
        phone: leadPhone,
        source: source,
        status: status,
        notes: notes,
        custom_fields: customFields,
      },
      inputData: {
        name: inputName !== undefined ? inputName : leadName,
        phone: leadPhone,
        notes: inputNotes !== undefined ? inputNotes : notes,
        custom_fields: inputCustomFields !== undefined ? inputCustomFields : customFields,
        status: inputStatus !== undefined ? inputStatus : status,
      }
    });

    if (leadId && (notes === undefined || customFields === undefined)) {
      try {
        const fullLead = await leadsApi.getLead(leadId);
        if (fullLead) {
          setEditLeadData((prev) => prev ? {
            ...prev,
            existingLead: {
              ...prev.existingLead,
              name: fullLead.name || prev.existingLead.name,
              status: fullLead.lead_status || prev.existingLead.status,
              source: (fullLead.source as string) || prev.existingLead.source,
              notes: fullLead.notes || undefined,
              custom_fields: fullLead.custom_fields || prev.existingLead.custom_fields,
            }
          } : null);
        }
      } catch (err) {
        console.error('Failed to fetch full lead details:', err);
      }
    }
  };

  const handleUpdateLeadSubmit = async (updates: Record<string, any>) => {
    if (!editLeadData || Object.keys(updates).length === 0) return;
    setIsUpdatingLead(true);
    try {
      const existing = editLeadData.existingLead;
      const updated = await leadsApi.updateLead(existing.id, updates);
      showNotification('Lead updated successfully', 'success');
      
      const finalLead = {
        ...existing,
        ...updated,
        id: String(existing.id),
        name: updated.name || updated.phone ,
        notes: updated.notes !== undefined ? updated.notes : existing.notes,
        custom_fields: updated.custom_fields || (updated as any).customFields || existing.custom_fields,
      };
      
      setEditLeadData(null);
      onSuccess(finalLead);
    } catch (err: any) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 
        (Array.isArray(detail) && detail.length > 0 && detail[0].msg) ? detail[0].msg.replace('Value error, ', '') : 'Failed to update lead';
      showNotification(msg, 'error');
    } finally {
      setIsUpdatingLead(false);
    }
  };

  const renderEditLeadDialog = () => {
    if (!editLeadData) return null;

    return (
      <UpdateExistingLeadDialog
        open={!!editLeadData}
        existingLead={editLeadData.existingLead}
        inputData={editLeadData.inputData}
        onConfirm={handleUpdateLeadSubmit}
        onCancel={() => setEditLeadData(null)}
        isLoading={isUpdatingLead}
        mode={leadDialogMode}
        smbId={smbId}
        customColumns={customColumns}
        customStatuses={customStatuses}
        setCustomStatuses={setCustomStatuses}
        onStatusUpdated={onStatusUpdated}
      />
    );
  };

  return {
    openEditLead,
    renderEditLeadDialog,
  };
};
